import { Outlet, NavLink, useNavigate, useLocation } from 'react-router-dom'
import { useState, useEffect, useCallback, type MouseEvent } from 'react'
import type { Project } from '@shared/types'
import { GCC_DEMO_MODE_BLOCKED_MESSAGE } from '@tn/shared'
import { useApi, useGenerationContext } from '../index'
import { GCC_APP_TITLE, gccDisplayName } from './gccDesignName'
import { deleteGccProject, gccDesignDeleteMessage, planGccDesignDelete } from './deleteGccProject'

/**
 * True while a design's generation hasn't finished or failed. List queries on
 * CosmosDB leave out gccData, so a finished design is also recognised by the
 * summary fields saved with it (the same test GccHome uses for its cards).
 */
function isGccProcessing(p: Project): boolean {
  if (p.gccData || p.gccError) return false
  return typeof p.gccRoleCount !== 'number' || typeof p.gccLabourSavingUSD !== 'number'
}

export default function GccLayout() {
  const [projects, setProjects] = useState<Project[]>([])
  const [isDemoActive, setIsDemoActive] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const api = useApi()
  const { generationCompletedAt, activeJobs } = useGenerationContext()

  useEffect(() => {
    const prev = document.title
    document.title = GCC_APP_TITLE
    return () => { document.title = prev }
  }, [])

  const fetchProjects = useCallback(async () => {
    const [all, demoState] = await Promise.all([api.listProjects(), api.getDemoState()])
    const demoActive = demoState?.active ?? false
    setIsDemoActive(demoActive)
    setProjects(all.filter(p => p.mode === 'gcc' && (demoActive || !p.isDemo)))
  }, [api])

  // Background refreshes: a failed one (e.g. a throttled backend) keeps the
  // current list and the next refresh tries again.
  useEffect(() => { void fetchProjects().catch(() => {}) }, [location.pathname, fetchProjects])

  useEffect(() => {
    if (!generationCompletedAt) return
    void fetchProjects().catch(() => {})
  }, [generationCompletedAt, fetchProjects])

  // Poll while any GCC project is still processing. Stops automatically once
  // every project has finished, failed or been deleted.
  useEffect(() => {
    if (!projects.some(isGccProcessing)) return
    const id = setInterval(() => void fetchProjects().catch(() => {}), 5000)
    return () => clearInterval(id)
  }, [projects, fetchProjects])

  const refreshProjects = fetchProjects

  const handleDelete = async (id: string, e: MouseEvent) => {
    e.preventDefault()
    e.stopPropagation()
    try {
      // The design's TN Analyses and combined dashboards go with it (WI 2494).
      const plan = await planGccDesignDelete(api, id, activeJobs)
      if (!window.confirm(gccDesignDeleteMessage(plan))) return
      await deleteGccProject(api, plan)
    } catch {
      alert('Failed to delete design. Please try again.')
    }
    void fetchProjects().catch(() => {})
  }

  return (
    <div className="flex h-screen overflow-hidden bg-gray-50">
      {/* Sidebar */}
      <aside className="w-64 flex-shrink-0 bg-white border-r border-gray-100 flex flex-col shadow-sm">
        {/* Logo */}
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="flex items-center gap-3">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center text-white text-[10px] font-bold"
              style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
            >
              GCC
            </div>
            <div>
              <div className="text-sm font-semibold text-gray-900 leading-tight">
                Global Capability Center
              </div>
              <div className="text-[10px] text-gray-400 leading-tight">Workforce Reinvention</div>
              <div data-testid="gcc-powered-by" className="text-[10px] text-gray-400 leading-tight mt-0.5">
                Powered by <span className="font-semibold" style={{ color: '#A100FF' }}>Talent Navigator</span>
              </div>
            </div>
          </div>
        </div>

        {/* New GCC Design CTA — disabled in demo mode (WI 2368) */}
        <div className="px-4 pt-4">
          <button
            onClick={() => navigate('/gcc/wizard')}
            disabled={isDemoActive}
            title={isDemoActive ? GCC_DEMO_MODE_BLOCKED_MESSAGE : undefined}
            className="w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-lg text-sm font-semibold text-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ background: 'linear-gradient(135deg, #A100FF, #7900B8)' }}
          >
            <span className="text-lg leading-none">+</span>
            New GCC Design
          </button>
        </div>

        {/* GCC project list */}
        <div className="flex-1 overflow-y-auto px-4 pt-4">
          <NavLink
            to="/gcc"
            end
            className={({ isActive }) =>
              `block px-2 mb-2 text-[10px] font-semibold uppercase tracking-widest ${isActive ? 'text-purple-600' : 'text-gray-400 hover:text-gray-600'}`
            }
          >
            My GCC Designs
          </NavLink>

          {projects.length === 0 ? (
            <div className="text-xs text-gray-400 px-2 py-4 text-center">
              No designs yet. Create your first!
            </div>
          ) : (
            <nav className="space-y-1">
              {projects.map(p => (
                <NavLink
                  key={p.id}
                  to={`/gcc/dashboard/${p.id}`}
                  className={({ isActive }) =>
                    `group flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                      isActive
                        ? 'bg-purple-50 text-purple-700 font-medium'
                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                    }`
                  }
                >
                  <span
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{ background: p.branding?.primaryColor || '#A100FF' }}
                  />
                  <span className="truncate flex-1">{gccDisplayName(p)}</span>
                  {p.isDemo && (
                    <span className="text-[8px] font-bold uppercase tracking-widest bg-purple-100 text-purple-500 px-1.5 py-0.5 rounded flex-shrink-0">
                      Demo
                    </span>
                  )}
                  <button
                    onClick={(e) => void handleDelete(p.id, e)}
                    title="Delete"
                    className="opacity-0 group-hover:opacity-100 text-gray-300 hover:text-red-400 text-base leading-none flex-shrink-0 transition-opacity"
                  >
                    ×
                  </button>
                </NavLink>
              ))}
            </nav>
          )}
        </div>

        {/* Bottom nav */}
        <div className="px-4 pb-4 pt-2 border-t border-gray-100 space-y-1">
          <button
            type="button"
            onClick={() => navigate('/gcc')}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-gray-500 hover:bg-gray-50 transition-all"
          >
            <span>🗂️</span> My GCC Designs
          </button>
          <NavLink
            to="/gcc/settings"
            className={({ isActive }) =>
              `flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm transition-all ${
                isActive ? 'bg-purple-50 text-purple-700' : 'text-gray-500 hover:bg-gray-50'
              }`
            }
          >
            <span>⚙️</span> Settings
          </NavLink>
          <button
            type="button"
            onClick={() => { window.location.href = '/auth/logout?reason=manual' }}
            className="w-full flex items-center gap-2.5 px-3 py-2.5 rounded-lg text-sm text-gray-500 hover:bg-gray-50 transition-all"
          >
            <span>🚪</span> Sign out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Positioned, so absolute children of a page clip here instead of making the window scroll (WI 2754) */}
        <main className="relative flex-1 overflow-y-auto">
          <Outlet context={{ refreshProjects, projects, isDemoActive }} />
        </main>
      </div>
    </div>
  )
}
